import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import PopupV3, { PopupTypes } from '../../../Popup/PopupV3';
import SwitchButton from '../../../Form/ToggleSwitch/SwitchButton';
import { getApiAxiosInstance } from '../../../../utils/network';
import { getRatingDetails, setRatingChoice } from '../../../../utils';
import { showErrorPopup } from '../../../../actions/notification-actions';
import { useCPPDynamicConfig } from '../../../hooks/useCPPDynamicConfig';
import useCollegeProspectAccess from '../../../hooks/useCollegeProspectAccess';
import { collegeProspectMaxAge, collegeProspectMinAge } from '../../../../utils/constants';
import styles from '../headerV3.scss';

const COLLEGE_PROSPECT_BADGE_ENDPOINT = '/v1/cpp/college-prospect-badge';

const BADGE_INFO_TITLE = 'About College Prospect Badge';
const BADGE_INFO_MESSAGE = '<p>The badge around your profile picture is a quick visual signal that you\'re a College Prospect — actively interested in playing tennis in college and open to being recruited.</p>' +
    '<p>College coaches scan a lot of profiles. The badge helps you stand out and tells them, at a glance, that outreach is welcome.</p>' +
    '<p>Make sure the rest of your profile (Academics, Primary Contact, Target College List) is up to date before you turn it on.</p>' +
    '<p>You can toggle the badge on or off anytime — turn it on when you\'re actively pursuing college tennis, and turn it off once you\'ve committed to a school or decided not to play at the college level.</p>';

const CollegeProspectBadgeToggle = ({
    profileData,
    isOwnProfile,
    isOn,
    onBadgeChange,
    inputId,
    'data-testid': dataTestId,
}) => {
    const dispatch = useDispatch();
    const { isCollegeProspectPowerEnabled } = useCPPDynamicConfig();
    const { isPowered, hasAnnualPower } = useCollegeProspectAccess();

    const [isSaving, setIsSaving] = useState(false);
    const [isInfoPopupOpen, setIsInfoPopupOpen] = useState(false);

    const isOverAgeCap = !!profileData?.age && profileData.age > collegeProspectMaxAge;
    const isUnderAgeMin = !!profileData?.age && profileData.age < collegeProspectMinAge;
    const isProgressionBallPlayer = getRatingDetails(setRatingChoice({ player: profileData || {} }), isPowered, true, false, false).showPBRRating;
    if (!isOwnProfile || !isCollegeProspectPowerEnabled || !hasAnnualPower || isOverAgeCap || isUnderAgeMin || isProgressionBallPlayer) {
        return null;
    }

    const handleToggle = async (event) => {
        if (isSaving) {
            return;
        }
        const nextIsOn = event.target.checked;
        const previousIsOn = isOn;
        onBadgeChange(nextIsOn);
        setIsSaving(true);
        try {
            await getApiAxiosInstance().put(COLLEGE_PROSPECT_BADGE_ENDPOINT, { displayBadge: nextIsOn });
        } catch (err) {
            onBadgeChange(previousIsOn);
            dispatch(showErrorPopup({
                message: 'We couldn\'t update your College Prospect badge. Please try again.',
                error: err,
            }));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.badgeToggleRow} data-testid={dataTestId}>
            <SwitchButton
                id={inputId}
                name={inputId}
                checked={isOn}
                onChange={handleToggle}
                theme="switch-button-flat-round"
                mode="switch"
                data-testid={dataTestId && `${dataTestId}.switch`}
            />
            <label
                className={styles.badgeToggleLabel}
                htmlFor={inputId}
                data-testid={dataTestId && `${dataTestId}.label`}
            >
                Display College Prospect Badge
            </label>
            <span
                role="button"
                aria-label="About College Prospect Badge"
                className={styles.badgeInfoIcon}
                onClick={() => setIsInfoPopupOpen(true)}
                data-testid={dataTestId && `${dataTestId}.info-btn`}
            />
            <PopupV3
                title={BADGE_INFO_TITLE}
                message={BADGE_INFO_MESSAGE}
                isOpen={isInfoPopupOpen}
                type={PopupTypes.INFO}
                onConfirmClick={() => setIsInfoPopupOpen(false)}
                onCloseButtonClick={() => setIsInfoPopupOpen(false)}
                confirmButtonLabel="Got it"
                data-testid={dataTestId && `${dataTestId}.info-popup`}
            />
        </div>
    );
};

CollegeProspectBadgeToggle.propTypes = {
    profileData: PropTypes.shape({
        age: PropTypes.number,
    }),
    isOwnProfile: PropTypes.bool,
    isOn: PropTypes.bool,
    onBadgeChange: PropTypes.func.isRequired,
    inputId: PropTypes.string,
    'data-testid': PropTypes.string,
};

CollegeProspectBadgeToggle.defaultProps = {
    profileData: null,
    isOwnProfile: false,
    isOn: false,
    inputId: 'college-prospect-badge-toggle',
    'data-testid': null,
};

export default CollegeProspectBadgeToggle;
